import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { PUBLIC_LANDING } from "@/constants/testIds";
import api, { formatApiError } from "@/lib/api";
import PublicChatWidget from "@/components/PublicChatWidget";
import DBReactivationSection from "@/components/app/DBReactivationSection";
import { toast, Toaster } from "sonner";
import {
  ArrowUpRight, Check, PhoneCall, Sparkles, ShieldCheck, Star,
  Wrench, Stethoscope, Scissors, Home as HomeIcon, ExternalLink,
  Loader2, Send, Plus, Minus,
} from "lucide-react";

/**
 * Public marketing site — Hybrid theme.
 *
 * Palette: light warm cream base + deep navy ink + coral primary + brass secondary + sage success.
 * Type:    Fraunces (display serif) + Inter (body) + JetBrains Mono (accents).
 * Layout:  editorial with Roman numerals, generous whitespace, per-industry cards.
 *
 * All live functionality preserved:
 * - GET /public/site-config for dynamic pricing + concierge agent
 * - POST /public/contact for contact form
 * - PublicChatWidget mounted with the agent
 * - All existing routes preserved (/for/:industry, /pricing-public, /directory, /login, etc.)
 */

const T = {
  // Dark-first AI-modern palette
  cream: "#0A0E1A",       // page background (deep space navy)
  canvas: "#0E1420",      // alternating section background
  panel: "#141B2D",       // card / panel surface
  ink: "#F5F7FA",         // primary text (near-white)
  inkSoft: "#D1D9E6",     // secondary text
  inkMuted: "#8B95A7",    // muted text
  inkFaint: "#5A6478",    // faintest text
  coral: "#FF6B47",       // primary accent (hot coral)
  coralDark: "#E5533B",
  coralSoft: "#3A1F1A",   // dark coral background wash
  brass: "#F5B840",       // secondary accent (bright brass)
  brassSoft: "#2A2416",
  sage: "#4ADE80",        // success (bright mint on dark)
  line: "#1E293B",        // border on dark
  lineSoft: "#141B2D",    // subtle divider
};

const FONT_BODY    = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_DISPLAY = "'Fraunces', 'Playfair Display', Georgia, serif";
const FONT_MONO    = "'JetBrains Mono', 'IBM Plex Mono', monospace";

const INDUSTRIES = [
  { icon: Wrench,       to: "/for/plumber",    id: "plumber",    label: "Plumbers",    tag: "Emergency service tuned" },
  { icon: Wrench,       to: "/for/hvac",       id: "hvac",       label: "HVAC",        tag: "Seasonal peak-load ready" },
  { icon: Wrench,       to: "/for/electrician",id: "electrician",label: "Electricians",tag: "Panel + EV specialists" },
  { icon: HomeIcon,     to: "/for/roofer",     id: "roofer",     label: "Roofers",     tag: "Storm-claim triage" },
  { icon: Stethoscope,  to: "/for/dentist",    id: "dentist",    label: "Dentists",    tag: "Appointment recall built-in" },
  { icon: Stethoscope,  to: "/for/lawyer",     id: "lawyer",     label: "Solo Lawyers",tag: "Intake-first, always" },
  { icon: HomeIcon,     to: "/for/insurance",  id: "insurance",  label: "Insurance",   tag: "Policy + quote flow" },
  { icon: Scissors,     to: "/for/cleaning",   id: "cleaning",   label: "Cleaning",    tag: "Recurring-schedule aware" },
];

const FALLBACK_PLANS = [
  { key: "starter",       name: "Starter",        price: 397, tag: "AI chat on your site",   bullets: ["Website preview (yours to keep)", "500 outbound emails / mo", "Basic pipeline"] },
  { key: "front_office",  name: "Front Office",   price: 697, tag: "The one everyone picks", bullets: ["24/7 voice agent", "Auto-booking to your calendar", "5,000 emails + SMS / mo", "Automated follow-up sequences"], best: true },
  { key: "multi_location",name: "Multi-Location", price: 997, tag: "2-5 locations",          bullets: ["Unlimited AI agents", "Per-location routing & analytics", "Done-for-you outreach", "Priority ops support"] },
];

/** Muted, loopable "AI concierge answering a call" scene for the hero.
 *  Pure CSS/SVG (no video asset needed) — pulsing rings around a phone icon,
 *  animated audio waveform bars, live-status dot. Prefers-reduced-motion respected.
 *  Click the phone core → plays an 8-sec AI voice sample via the browser's
 *  Web Speech API (SpeechSynthesis). No external audio file needed. */
function HeroCallLoop() {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(true);
  const [mp3Available, setMp3Available] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    setSupported(!!(window.speechSynthesis && window.SpeechSynthesisUtterance));
    // Check if the ElevenLabs-generated greeting MP3 is present
    fetch("/audio/hero-greeting.mp3", { method: "HEAD" })
      .then((r) => setMp3Available(r.ok && (r.headers.get("content-type") || "").includes("audio")))
      .catch(() => setMp3Available(false));
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  const playSample = () => {
    // Prefer the real ElevenLabs MP3 (matches production Vapi voice)
    if (mp3Available) {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setSpeaking(false);
        return;
      }
      const a = new Audio("/audio/hero-greeting.mp3");
      audioRef.current = a;
      a.onended  = () => setSpeaking(false);
      a.onerror  = () => { setSpeaking(false); setMp3Available(false); };
      setSpeaking(true);
      a.play().catch(() => { setSpeaking(false); setMp3Available(false); });
      return;
    }
    // Fallback: browser SpeechSynthesis
    if (!supported) return;
    const synth = window.speechSynthesis;
    if (synth.speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const utter = new window.SpeechSynthesisUtterance(
      "Hi, thanks for calling. This is your AI concierge. I can book appointments, answer service questions, and follow up automatically. How can I help you today?"
    );
    utter.rate  = 1.02;
    utter.pitch = 1.05;
    utter.volume = 1.0;
    const voices = synth.getVoices();
    const preferred = voices.find((v) => /en(-|_)?(US|GB|AU)/i.test(v.lang) && /female|samantha|karen|jenny|zira|joanna/i.test(v.name))
                  || voices.find((v) => /^en/i.test(v.lang))
                  || voices[0];
    if (preferred) utter.voice = preferred;
    utter.onend    = () => setSpeaking(false);
    utter.onerror  = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utter);
    setTimeout(() => { try { synth.cancel(); } catch { /* noop */ } setSpeaking(false); }, 9000);
  };

  return (
    <div
      data-testid="hero-call-loop"
      style={{
        position: "absolute",
        right: "-40px",
        top: "50%",
        transform: "translateY(-50%)",
        width: 520,
        height: 520,
        opacity: 0.9,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      className={`hero-call-loop ${speaking ? "is-speaking" : ""}`}
    >
      {/* Expanding rings */}
      <span className="hero-call-ring hero-call-ring--1" />
      <span className="hero-call-ring hero-call-ring--2" />
      <span className="hero-call-ring hero-call-ring--3" />

      {/* Phone core — click to play voice sample */}
      <button
        type="button"
        onClick={playSample}
        aria-label={speaking ? "Stop AI voice sample" : "Play AI voice sample"}
        data-testid="hero-call-play-btn"
        className="hero-call-core hero-call-core--interactive"
        style={{ background: "transparent", border: "none", padding: 0, cursor: supported ? "pointer" : "default" }}
      >
        <PhoneCall size={38} strokeWidth={1.8} color={T.coral} />
        {/* Waveform bars */}
        <div className="hero-call-wave" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <span key={i} className="hero-call-wave-bar" style={{ animationDelay: `${i * 0.11}s` }} />
          ))}
        </div>
        {/* Hover tooltip — only when idle */}
        {supported && !speaking && (
          <span className="hero-call-tip" aria-hidden="true" data-testid="hero-call-tip">
            🔊 Click to hear the AI voice
          </span>
        )}
      </button>
    </div>
  );
}


/** Small "dispatch ticker" showing rotating live-activity messages under the
 *  hero — pure social proof, no backend call. Rotates every 5s. */
function DispatchTicker() {
  const items = [
    "AI agent answered a call 14 seconds ago",
    "AI agent booked an appointment for a plumber in Long Beach",
    "AI agent texted back a missed caller in under 30 seconds",
    "AI agent qualified a new dental patient overnight",
    "AI agent handled 3 after-hours calls last night",
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      data-testid="hero-dispatch-ticker"
      style={{
        marginTop: 40,
        maxWidth: 640,
        background: T.panel,
        border: `1px solid ${T.line}`,
        padding: "12px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 20,
        fontFamily: FONT_MONO,
        fontSize: 11,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: T.inkMuted,
        position: "relative",
      }}
    >
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: T.sage, flexShrink: 0, boxShadow: `0 0 0 4px rgba(74,222,128,0.15)` }} />
        <span data-testid="dispatch-ticker-msg" style={{ color: T.ink, textTransform: "none", letterSpacing: 0, fontFamily: FONT_BODY, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {items[idx]}
        </span>
      </div>
      <span style={{ color: T.brass, fontFamily: FONT_MONO, fontSize: 10, flexShrink: 0 }}>SETUP IN DAYS, NOT WEEKS</span>
    </div>
  );
}


/** Simple FAQ accordion — one item open at a time. Draws visitors past the
 *  pricing questions that usually block conversion. */
function FaqAccordion() {
  const [openIdx, setOpenIdx] = useState(0);
  const items = [
    { q: "Is this actually free?", a: "Yes — the 4-section preview website is completely free. You keep it whether or not you subscribe. Setup for the paid AI receptionist plan starts at $750 (Starter) with monthly service from $397/mo — cancel anytime with 30 days' notice." },
    { q: "Does the AI agent sound robotic?", a: "Natural, human-like voice. Around 80% of callers don't realize they're talking to AI. Hover the phone icon in the hero to hear a live sample." },
    { q: "How long does setup take?", a: "5 business days for standard plans. Discovery call → we build & configure → you test live → we forward your number. Zero technical work on your side." },
    { q: "What if I already have a website?", a: "No problem. We can either replace it (recommended if it's outdated) or leave it alone and just install the AI receptionist. You choose." },
    { q: "What if the AI can't answer something?", a: "It gracefully takes a message with the caller's name, phone, and reason — texted straight to you within 60 seconds. Never a dead end." },
    { q: "Do you handle Spanish or other languages?", a: "Yes — Spanish is standard on Front Office and Multi-Location. Other languages available on request." },
    { q: "How do you charge — subscription or per call?", a: "Flat monthly, no per-call fees. What's included is on the pricing page. Overages are billed at cost with no markup." },
    { q: "What's the $69 Live Site Upgrade about?", a: "Optional. Adds 7 more conversion sections (comparison table, testimonials, FAQ, service areas, guarantee, gallery, reviews widget) on top of your free 4-section site. Not required — the free site works on its own." },
  ];
  return (
    <section id="faq" style={{ padding: "104px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}` }}>
      <div style={container()}>
        <div style={{ marginBottom: 48, maxWidth: 760 }}>
          <div style={eyebrow()}>Straight answers</div>
          <h2 style={h2()}>
            Frequently asked <em style={italicAccent(T.brass)}>questions.</em>
          </h2>
        </div>
        <div style={{ borderTop: `1px solid ${T.line}` }} data-testid="landing-faq">
          {items.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={i} style={{ borderBottom: `1px solid ${T.line}` }}>
                <button
                  type="button"
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  style={{
                    width: "100%", padding: "22px 0", display: "flex", alignItems: "center", justifyContent: "space-between",
                    background: "transparent", border: "none", color: T.ink, cursor: "pointer", textAlign: "left",
                    fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, gap: 20,
                  }}
                >
                  <span>{it.q}</span>
                  <span style={{ flexShrink: 0, color: open ? T.coral : T.inkMuted, display: "inline-flex" }}>
                    {open ? <Minus size={18} /> : <Plus size={18} />}
                  </span>
                </button>
                {open && (
                  <div
                    data-testid={`faq-answer-${i}`}
                    style={{ padding: "0 0 22px", fontSize: 15, lineHeight: 1.65, color: T.inkMuted, maxWidth: 820 }}
                  >
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


/** Slow left-scrolling row of testimonials. Duplicates the list twice so
 *  the marquee loops seamlessly. Pauses on hover; respects reduced motion. */
function TestimonialMarquee() {
  const items = [
    { quote: "First week live, the AI caught 3 after-hours calls I would have missed. Booked 2 of them. Paid for a year of subscription in one weekend.",
      name: "Marcus S.", role: "Owner", biz: "Sullivan & Sons Plumbing · Long Beach, CA" },
    { quote: "My front desk was drowning. The AI receptionist now handles routing, reschedules, and Spanish callers. My staff finally gets lunch.",
      name: "Dr. Priya R.", role: "Practice Owner", biz: "Cedar Family Dental · Austin, TX" },
    { quote: "Went from 27% missed calls to nearly zero. Two new AC installs in the first month traced directly to calls I would have never returned.",
      name: "Tommy V.", role: "Owner", biz: "West Coast HVAC · San Diego, CA" },
    { quote: "The reactivation engine texted 400 old clients and 32 rebooked. That was $18k of found revenue in a week we had almost given up on.",
      name: "Alicia M.", role: "Owner", biz: "Alicia's Blow Dry Bar · Denver, CO" },
    { quote: "I was skeptical about AI on the phone. My customers can't tell — and if they ask for me, it forwards clean. Zero friction.",
      name: "Rob K.", role: "Owner", biz: "K&K Auto Detailing · Phoenix, AZ" },
    { quote: "Best $697/mo I spend. It's my third employee — one that never calls in sick and never fumbles a booking.",
      name: "Jenna T.", role: "Owner", biz: "Bright Smile Med Spa · Nashville, TN" },
    { quote: "Setup took a week. The site preview alone was worth the setup fee — but the voice agent is what makes it stick.",
      name: "David L.", role: "Owner", biz: "Foundation First Roofing · Sacramento, CA" },
    { quote: "I stopped answering my business line at dinner. Family time back. Revenue up. That's the whole pitch and it's true.",
      name: "Karen O.", role: "Owner", biz: "Sunrise Cleaning Co. · Portland, OR" },
  ];
  // Duplicate for seamless loop
  const loop = [...items, ...items];
  return (
    <section
      data-testid="testimonial-marquee"
      style={{ padding: "88px 0 24px", background: T.canvas, borderTop: `1px solid ${T.lineSoft}`, overflow: "hidden" }}
    >
      <div style={container()}>
        <div style={{ marginBottom: 40, maxWidth: 760 }}>
          <div style={eyebrow()}>What owners say</div>
          <h2 style={h2()}>
            The last mile matters — <em style={italicAccent(T.brass)}>they picked us and stuck.</em>
          </h2>
        </div>
      </div>
      <div className="testimonial-marquee-viewport" data-testid="testimonial-marquee-track">
        <div className="testimonial-marquee-track">
          {loop.map((t, i) => (
            <figure key={i} className="testimonial-card">
              <div className="testimonial-quote-mark" aria-hidden="true">&ldquo;</div>
              <blockquote>{t.quote}</blockquote>
              <figcaption>
                <div className="testimonial-name">{t.name}</div>
                <div className="testimonial-role">{t.role} · {t.biz}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}


/** One-shot "Claim your free website" mini-form — warm-cream card sitting
 *  just above the contact section. 4 fields (business, name, phone, email)
 *  hitting the existing /public/contact endpoint with source="free_site_claim". */
function FreeSiteClaimStrip() {
  const [f, setF] = useState({ business: "", name: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const claim = async (e) => {
    e.preventDefault();
    if (!f.business.trim() || !f.name.trim() || !f.email.trim()) {
      return toast.error("Business, name, and email are required.");
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) {
      return toast.error("Please enter a valid email.");
    }
    setBusy(true);
    try {
      await api.post("/public/contact", {
        name:     f.name.trim(),
        email:    f.email.trim(),
        phone:    f.phone.trim(),
        business: f.business.trim(),
        industry: "",
        message:  "Free website claim — inline strip on landing page",
      });
      setDone(true);
      toast.success("Got it — we'll email your preview link shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="free-site-claim-strip"
      style={{ padding: "72px 0 40px", background: T.cream }}
    >
      <div style={container()}>
        <div
          style={{
            background: "#F7F4EE",
            color: "#0A192F",
            padding: "44px 40px",
            display: "grid",
            gridTemplateColumns: "1fr 1.4fr",
            gap: 40,
            alignItems: "center",
            border: `1px solid rgba(10,25,48,0.08)`,
            boxShadow: "0 30px 60px -20px rgba(10,14,26,0.5)",
          }}
        >
          <div>
            <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700 }}>
              No cost · No obligation
            </div>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 34, lineHeight: 1.05, letterSpacing: "-0.02em", fontWeight: 400, margin: "12px 0 12px", color: "#0A192F" }}>
              Claim your <em style={{ fontStyle: "italic", color: T.coral }}>free website</em> and AI agent.
            </h3>
            <p style={{ fontSize: 14, color: "#3D4756", lineHeight: 1.6, margin: 0 }}>
              Tell us about your business — we'll build a preview site and reach out within one business day. Yours to keep, whether or not you subscribe.
            </p>
          </div>

          {done ? (
            <div
              data-testid="free-site-claim-done"
              style={{
                background: "rgba(74,222,128,0.10)",
                border: "1px solid rgba(4,120,87,0.35)",
                padding: "28px 24px",
                textAlign: "center",
              }}
            >
              <Check size={36} color="#047857" style={{ margin: "0 auto 10px" }} strokeWidth={2} />
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500, fontStyle: "italic", color: "#0A192F" }}>You&apos;re on the list.</div>
              <p style={{ fontSize: 13, color: "#3D4756", marginTop: 8, lineHeight: 1.55 }}>
                We&apos;ll email your preview site link within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={claim} style={{ display: "grid", gap: 10 }} data-testid="free-site-claim-form">
              <label style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 10, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: "#5A6478", fontWeight: 700 }}>Business name</span>
                <input
                  required
                  value={f.business}
                  onChange={(e) => setF({ ...f, business: e.target.value })}
                  placeholder="e.g. Sullivan & Sons Plumbing"
                  data-testid="claim-business"
                  style={claimInput()}
                />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <label style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 10, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: "#5A6478", fontWeight: 700 }}>Your name</span>
                  <input
                    required
                    value={f.name}
                    onChange={(e) => setF({ ...f, name: e.target.value })}
                    placeholder="Full name"
                    data-testid="claim-name"
                    style={claimInput()}
                  />
                </label>
                <label style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 10, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: "#5A6478", fontWeight: 700 }}>Phone</span>
                  <input
                    value={f.phone}
                    onChange={(e) => setF({ ...f, phone: e.target.value })}
                    placeholder="(000) 000-0000"
                    data-testid="claim-phone"
                    style={claimInput()}
                  />
                </label>
              </div>
              <label style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 10, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: "#5A6478", fontWeight: 700 }}>Email</span>
                <input
                  required
                  type="email"
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                  placeholder="you@yourbusiness.com"
                  data-testid="claim-email"
                  style={claimInput()}
                />
              </label>
              <button
                type="submit"
                disabled={busy}
                data-testid="claim-submit"
                style={{
                  marginTop: 6,
                  background: T.coral,
                  color: "#F7F4EE",
                  border: "none",
                  padding: "14px 22px",
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: FONT_MONO,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  cursor: busy ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "Sending…" : "Claim my free site"} <ArrowUpRight size={15} />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function claimInput() {
  return {
    background: "#FFFFFF",
    color: "#0A192F",
    border: "1px solid rgba(10,25,48,0.14)",
    padding: "11px 13px",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    width: "100%",
  };
}


export default function PublicLanding() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    // Cache-bust so the landing page ALWAYS reflects the latest admin save
    // (Settings → Public pricing panel). Also refetch when the user returns
    // to the tab so a browser session mid-edit self-heals.
    const load = () => api.get(`/public/site-config?t=${Date.now()}`).then(({ data }) => {
      if (data.plans?.length) setPlans(normalizePlans(data.plans));
      if (data.agent) setAgent(data.agent);
    }).catch(() => {});
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", industry: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      toast.success("Got it. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goSignIn = () => user ? navigate("/dashboard") : navigate("/login");

  // Free-audit ribbon — dismissible for 7 days via localStorage
  const [showRibbon, setShowRibbon] = useState(true);
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem("cor_ribbon_dismissed_until") || "0", 10);
      if (until && until > Date.now()) setShowRibbon(false);
    } catch { /* private mode */ }
    // Log a single impression per page load (fire-and-forget)
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/ribbon-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "impression", variant: "free-audit-v1", path: window.location.pathname }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* silent */ }
  }, []);
  const logRibbon = (event) => {
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/ribbon-click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event, variant: "free-audit-v1", path: window.location.pathname }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* silent */ }
  };
  const dismissRibbon = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem("cor_ribbon_dismissed_until", String(Date.now() + sevenDays));
    } catch { /* private mode */ }
    logRibbon("dismiss");
    setShowRibbon(false);
  };

  return (
    <div
      data-testid={PUBLIC_LANDING.container}
      style={{ background: T.cream, color: T.ink, fontFamily: FONT_BODY, minHeight: "100vh" }}
    >
      <Toaster position="top-right" richColors />

      {/* FREE-AUDIT RIBBON — sits above nav to convert casual visitors */}
      {showRibbon && (
      <Link
        to="/free-audit"
        data-testid="free-audit-ribbon"
        onClick={() => logRibbon("click")}
        style={{
          display: "block",
          background: "linear-gradient(90deg, #E24E1B 0%, #FF6B47 100%)",
          color: "#FFFFFF",
          textDecoration: "none",
          textAlign: "center",
          padding: "10px 40px 10px 16px",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.2,
          position: "sticky",
          top: 0,
          zIndex: 40,
          height: 40,
          lineHeight: "20px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span className="cor-ribbon-desktop" style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "nowrap", justifyContent: "center" }}>
          <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5, opacity: 0.85, background: "rgba(255,255,255,0.18)", padding: "3px 8px", borderRadius: 3 }}>Free · 15 sec</span>
          <span>See if AI search actually recommends your business — 3 real queries, zero credit card.</span>
          <span style={{ textDecoration: "underline", fontWeight: 700 }}>Try it free →</span>
        </span>
        <span className="cor-ribbon-mobile" style={{ display: "none", alignItems: "center", gap: 6, justifyContent: "center" }}>
          <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, opacity: 0.85, background: "rgba(255,255,255,0.22)", padding: "2px 6px", borderRadius: 3 }}>Free</span>
          <span>AI Visibility audit</span>
          <span style={{ textDecoration: "underline", fontWeight: 700 }}>Try it →</span>
        </span>
        <button
          onClick={dismissRibbon}
          aria-label="Dismiss for 7 days"
          data-testid="free-audit-ribbon-dismiss"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(255,255,255,0.15)",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 3,
            width: 24,
            height: 24,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Dismiss for 7 days"
        >
          ✕
        </button>
      </Link>
      )}
      <style>{`
        @media (max-width: 720px) {
          .cor-ribbon-desktop { display: none !important; }
          .cor-ribbon-mobile  { display: inline-flex !important; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{ borderBottom: `1px solid ${T.line}`, background: "rgba(10,14,26,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", position: "sticky", top: showRibbon ? 40 : 0, zIndex: 30 }}>
        <div style={container()}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 76, gap: 16, flexWrap: "nowrap" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none", flexShrink: 0 }} data-testid="public-brand-logo">
              <img
                src="/logo-on-dark.png"
                onError={(e) => { e.currentTarget.src = "/logo.png"; }}
                alt="Clients OnRamp"
              style={{ height: 52, width: "auto", display: "block", objectFit: "contain" }}
              />
            </Link>
            <div style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 13, flexWrap: "nowrap", whiteSpace: "nowrap" }}>
              <button onClick={() => scrollTo("how")} style={navLink()} data-testid="nav-how">How it works</button>
              <Link to="/industries" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-industries">Industries</Link>
              <Link to="/preview/hybrid" target="_blank" rel="noopener" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-see-demo">See a demo</Link>
              <Link to="/pricing-public" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-pricing">Pricing</Link>
              <Link to="/packages" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-packages">Packages</Link>
              <Link to="/get-started" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-get-started">Get started</Link>
              <Link to="/onboarding-public" style={{ ...navLink(), textDecoration: "none" }} data-testid="nav-onboarding">Onboarding</Link>
              <button onClick={() => scrollTo("contact")} style={navLink()} data-testid="nav-contact">Contact</button>
              <a href="tel:+15625743752" data-testid="nav-tel" style={{ ...navLink(), textDecoration: "none", color: T.brass, fontWeight: 600, whiteSpace: "nowrap" }}>
                📞 (562) 574-3752
              </a>
              <Link to="/book" style={{ ...btnCoral(), whiteSpace: "nowrap", padding: "10px 16px", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="nav-book-demo">
                Book demo <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ padding: "128px 0 96px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -100, right: -150, width: 700, height: 700, background: "radial-gradient(circle, rgba(229,83,59,0.08) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
        <div style={{ position: "absolute", bottom: -200, left: -100, width: 500, height: 500, background: "radial-gradient(circle, rgba(200,149,67,0.10) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />

        {/* Muted AI-concierge call-answering loop — pulsing rings + audio waveform */}
        <HeroCallLoop />

        <div style={container()}>
          <div style={{ maxWidth: 880, position: "relative", zIndex: 1 }}>
            <div style={trustChip()}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: T.sage, boxShadow: `0 0 0 4px rgba(107,142,90,0.2)` }} />
              AI voice + chat agents for local service businesses
            </div>

            <h1 style={{
              fontFamily: FONT_DISPLAY,
              fontSize: "clamp(56px, 7.5vw, 116px)",
              lineHeight: 0.98,
              letterSpacing: "-0.035em",
              fontWeight: 400,
              margin: "32px 0 24px",
              color: T.ink,
            }} data-testid="hero-headline">
              Your business never<br />
              misses another <em style={{ fontStyle: "italic", color: T.coral, fontWeight: 400 }}>call.</em>
            </h1>

            <p style={{ fontSize: 22, lineHeight: 1.5, color: T.inkMuted, maxWidth: 640, margin: 0 }}>
              An AI voice agent that answers every call, books the appointment, and follows up
              automatically &mdash; 24/7. You keep doing the job you&apos;re good at.
            </p>

            <div style={{ marginTop: 44, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <Link to="/audit" style={{ ...btnCoral(), padding: "16px 26px", fontSize: 15, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="hero-cta-free-audit">
                <Sparkles size={17} /> Get Your Free AI Visibility Audit
              </Link>
              <Link to="/book" style={{ ...btnInk(), padding: "16px 26px", fontSize: 15, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }} data-testid="hero-cta-book">
                Book a demo call <ArrowUpRight size={17} />
              </Link>
              <button onClick={() => scrollTo("how")} style={{ background: "transparent", color: T.ink, border: "1px solid " + T.ink + "22", padding: "16px 26px", fontSize: 15, cursor: "pointer" }} data-testid="hero-cta-howitworks">
                See how it works
              </button>
              {agent && (
                <div style={{ fontSize: 13, color: T.inkMuted, marginLeft: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 6, height: 6, background: T.sage, borderRadius: 999 }} />
                  Or <strong style={{ color: T.ink }}>chat with our AI now</strong> &mdash; same tech we install.
                </div>
              )}
            </div>

            <div style={{ marginTop: 24, fontSize: 13, color: T.inkFaint, display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><ShieldCheck size={14} color={T.sage} /> Month-to-month · 30-day out</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={14} color={T.brass} /> Live in 5 business days</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Star size={14} color={T.coral} fill={T.coral} /> Preview site free forever</span>
            </div>

            {/* Live dispatch ticker — rotating social proof */}
            <DispatchTicker />
          </div>

          {/* Metric card */}
          <div style={{
            marginTop: 96, background: T.ink, color: T.cream,
            padding: "36px 44px",
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 40,
            position: "relative", boxShadow: "0 30px 60px -20px rgba(10,25,48,0.25)",
          }}>
            <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: 40, background: T.coral }} />
            {[
              { k: "27%",   v: "of small-business calls unanswered", color: T.coral },
              { k: "78%",   v: "of callers won't leave a voicemail", color: T.brass },
              { k: "$200+", v: "avg value of one missed call",       color: T.sage },
              { k: "3-8×",  v: "typical ROI in first 90 days",       color: T.coral },
            ].map((s) => (
              <div key={s.k} data-testid={`stat-${s.k.replace(/[^a-z0-9]/gi,'')}`}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 48, fontWeight: 400, letterSpacing: "-0.02em", color: s.color, lineHeight: 1 }}>{s.k}</div>
                <div style={{ fontSize: 11, color: T.inkMuted, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 12 }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ padding: "112px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}`, borderBottom: `1px solid ${T.lineSoft}` }}>
        <div style={container()}>
          <div style={{ marginBottom: 72, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "end" }}>
            <div>
              <div style={eyebrow()}>The arc</div>
              <h2 style={h2()}>
                From <em style={italicAccent(T.brass)}>ring</em><br />
                to <em style={italicAccent(T.coral)}>revenue.</em>
              </h2>
            </div>
            <p style={{ fontSize: 18, color: T.inkMuted, lineHeight: 1.65, margin: 0 }}>
              Most small businesses lose <strong style={{ color: T.ink }}>27%</strong> of inbound calls
              because owners are on the truck, in surgery, or in court. We insert an AI concierge
              between the ring and the voicemail &mdash; and change the math.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 1, background: T.line }}>
            {[
              { n: "I.",   t: "Preview website in 24h",    d: "AI copy tuned to your services and city. Yours to keep, whether or not we go further." },
              { n: "II.",  t: "Voice + chat agent live",   d: "Trained on your hours, pricing, services. Answers on the first ring, all day, every day." },
              { n: "III.", t: "Follow-ups run themselves", d: "Missed calls get texted back. Cold leads get nurtured. You just close the ones that convert." },
              { n: "IV.",  t: "Weekly digest arrives",     d: "How many calls answered, appointments booked, jobs saved. Every Monday, in your inbox." },
            ].map((step) => (
              <div key={step.n} style={{ background: T.panel, padding: "40px 32px" }} data-testid={`step-${step.n.replace('.','')}`}>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 52, color: T.coral, fontStyle: "italic", fontWeight: 400, lineHeight: 1 }}>{step.n}</div>
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em", marginTop: 24, color: T.ink }}>{step.t}</div>
                <p style={{ marginTop: 12, color: T.inkMuted, fontSize: 15, lineHeight: 1.65, margin: "12px 0 0" }}>{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" style={{ padding: "104px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 760, marginBottom: 48 }}>
            <div style={eyebrow()}>Built for the trades</div>
            <h2 style={h2()}>
              Tuned <em style={italicAccent(T.brass)}>per industry</em> &mdash; not one-size-fits-all.
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {INDUSTRIES.map((x) => (
              <Link
                key={x.id}
                to={x.to}
                data-testid={`industry-${x.id}`}
                style={{
                  padding: "28px 24px", background: T.panel, border: `1px solid ${T.line}`,
                  cursor: "pointer", transition: "all 200ms", position: "relative", overflow: "hidden",
                  textDecoration: "none", color: "inherit", display: "block",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.coral; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 24px -8px rgba(229,83,59,0.15)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 500, color: T.ink }}>{x.label}</div>
                <div style={{ fontSize: 12, color: T.inkFaint, marginTop: 6, fontFamily: FONT_MONO, letterSpacing: "0.06em", textTransform: "uppercase" }}>{x.tag}</div>
                <div style={{ marginTop: 22, fontSize: 12, color: T.coral, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                  See page <ArrowUpRight size={13} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AI VISIBILITY SERVICE BLOCK */}
      <section id="ai-visibility" style={{ padding: "112px 0", background: T.canvas, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`, position: "relative", overflow: "hidden" }}>
        <div aria-hidden="true" style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 800, height: 400,
          background: "radial-gradient(ellipse at center, rgba(255,107,71,0.10), transparent 70%)",
          filter: "blur(60px)", pointerEvents: "none",
        }} />
        <div style={{ ...container(), position: "relative", zIndex: 1 }}>
          <div style={{ maxWidth: 780, margin: "0 auto 64px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.coralSoft, color: T.coral, padding: "6px 14px", borderRadius: 999, fontSize: 12, fontFamily: FONT_MONO, letterSpacing: 2, textTransform: "uppercase", fontWeight: 600, border: `1px solid ${T.coral}44`, marginBottom: 20 }}>
              <Sparkles size={14} /> NEW · AI VISIBILITY SUITE
            </div>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, color: T.ink }}>
              Get found when customers ask <em style={{ color: T.coral, fontStyle: "italic", fontWeight: 400 }}>ChatGPT</em>, <em style={{ color: T.brass, fontStyle: "italic", fontWeight: 400 }}>Gemini</em>, and <em style={{ color: T.sage, fontStyle: "italic", fontWeight: 400 }}>Perplexity</em>.
            </h2>
            <p style={{ fontSize: 18, color: T.inkSoft, lineHeight: 1.6, marginTop: 20, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
              AI assistants now answer 40% of "best plumber near me" questions before customers ever open Google. If they aren't citing your business, they're citing your competitor. Our AI Visibility Suite fixes that.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24, maxWidth: 960, margin: "0 auto" }}>
            {/* Tier 1: Audit */}
            <div style={{ background: T.panel, border: `1px solid ${T.line}`, padding: 36, position: "relative", overflow: "hidden" }} data-testid="ai-visibility-tier-audit">
              <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: 3, color: T.brass, textTransform: "uppercase", marginBottom: 8 }}>Tier 1 · Diagnostic</div>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 500, margin: "0 0 8px", color: T.ink, letterSpacing: "-0.01em" }}>AI Visibility Audit</h3>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "16px 0 20px" }}>
                <span style={{ fontSize: 44, fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>$79</span>
                <span style={{ color: T.inkMuted, fontFamily: FONT_MONO, fontSize: 13 }}>/mo</span>
                <span style={{ color: T.inkFaint, fontSize: 12, marginLeft: 8 }}>+ $199 setup</span>
              </div>
              <p style={{ color: T.inkSoft, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Personalized 30-point report showing exactly where you stand in AI + Google search. Delivered as a branded PDF within 48 hours.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", fontSize: 13, color: T.inkSoft, lineHeight: 1.9 }}>
                {["Executive summary written by Claude AI","Top customer questions mapped to your gaps","Competitor benchmark (top 3 rivals)","Prioritized P0/P1/P2 action plan","PDF delivered to your inbox"].map((f) => (
                  <li key={f} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Check size={14} color={T.sage} style={{ marginTop: 4, flexShrink: 0 }} /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/audit" style={{ ...btnInk(), display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", width: "100%", justifyContent: "center", background: "transparent", color: T.ink, border: `1px solid ${T.inkMuted}` }} data-testid="ai-visibility-audit-cta">
                Start free teaser <ArrowUpRight size={15} />
              </Link>
            </div>

            {/* Tier 2: Pro - featured */}
            <div style={{ background: T.panel, border: `2px solid ${T.coral}`, padding: 36, position: "relative", overflow: "hidden", boxShadow: `0 0 60px -20px ${T.coral}66` }} data-testid="ai-visibility-tier-pro">
              <div style={{ position: "absolute", top: 0, right: 0, background: T.coral, color: T.ink, fontSize: 10, fontFamily: FONT_MONO, letterSpacing: 2, padding: "6px 14px", fontWeight: 700 }}>MOST POPULAR</div>
              <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: 3, color: T.coral, textTransform: "uppercase", marginBottom: 8 }}>Tier 2 · Done-for-you</div>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 500, margin: "0 0 8px", color: T.ink, letterSpacing: "-0.01em" }}>AI Visibility Pro</h3>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "16px 0 20px" }}>
                <span style={{ fontSize: 44, fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>$299</span>
                <span style={{ color: T.inkMuted, fontFamily: FONT_MONO, fontSize: 13 }}>/mo</span>
                <span style={{ color: T.inkFaint, fontSize: 12, marginLeft: 8 }}>+ $399 setup</span>
              </div>
              <p style={{ color: T.inkSoft, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
                Everything in the Audit — plus we <em style={{ color: T.brass }}>implement every fix</em> for you. Re-audits every 90 days included.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", fontSize: 13, color: T.inkSoft, lineHeight: 1.9 }}>
                {["Everything in the Audit tier","We claim + optimize your Google Business Profile","Schema + local SEO structure deployed","Content refresh across your site","Quarterly re-audit + trend tracking","Direct Slack channel with our team"].map((f) => (
                  <li key={f} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Check size={14} color={T.coral} style={{ marginTop: 4, flexShrink: 0 }} /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/audit" style={{ ...btnCoral(), display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", width: "100%", justifyContent: "center" }} data-testid="ai-visibility-pro-cta">
                Get started <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 40, fontSize: 13, color: T.inkMuted, fontFamily: FONT_MONO }}>
            Not sure yet? <Link to="/audit" style={{ color: T.coral, textDecoration: "none", fontWeight: 600 }}>Get a free 1-page teaser PDF →</Link>
          </div>

          {/* Live proof widget: mock ChatGPT recommending competitors */}
          <div style={{ marginTop: 72, maxWidth: 780, marginLeft: "auto", marginRight: "auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ ...eyebrow(), color: T.brass, marginBottom: 12 }}>SEE IT LIVE</div>
              <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(24px, 3vw, 36px)", fontWeight: 500, color: T.ink, margin: 0, letterSpacing: "-0.01em" }}>
                What ChatGPT says when your customers ask for the best <em style={{ color: T.coral, fontStyle: "italic" }}>{"{your industry}"}</em>.
              </h3>
              <p style={{ color: T.inkSoft, fontSize: 14, marginTop: 10 }}>Type your industry + city — we'll show you exactly who AI is recommending instead of you.</p>
            </div>
            <CompetitorDemoWidget />
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" style={{ padding: "104px 0", background: T.canvas, borderTop: `1px solid ${T.lineSoft}` }}>
        <div style={container()}>
          <div style={{ marginBottom: 56, maxWidth: 720 }}>
            <div style={eyebrow()}>Pricing</div>
            <h2 style={h2()}>
              One monthly rate. <em style={italicAccent(T.coral)}>No contract.</em>
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            {plans.map((p) => {
              const isBest = !!p.best;
              const accent = p.key === "front_office" ? T.coral : p.key === "multi_location" ? T.sage : T.brass;
              const bg = isBest ? T.ink : T.panel;
              const ink = isBest ? T.cream : T.ink;
              return (
                <div key={p.key} data-testid={`plan-${p.key}`} style={{
                  background: bg, color: ink, padding: "40px 32px",
                  border: `1px solid ${isBest ? T.ink : T.line}`, position: "relative",
                  boxShadow: isBest ? "0 30px 60px -20px rgba(10,25,48,0.35)" : "none",
                  transform: isBest ? "scale(1.02)" : "none",
                }}>
                  {isBest && (
                    <div style={{ position: "absolute", top: -1, left: -1, background: accent, color: T.cream, padding: "6px 14px", fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700 }}>
                      Best fit
                    </div>
                  )}
                  <div style={{ fontSize: 12, fontFamily: FONT_MONO, letterSpacing: "0.09em", textTransform: "uppercase", color: accent, fontWeight: 600 }}>{p.tag}</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: 400, marginTop: 10, fontStyle: "italic", color: ink }}>{p.name}</div>
                  <div style={{ marginTop: 24, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: 76, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 1, color: ink }}>${p.price}</div>
                    <div style={{ color: T.inkFaint, fontSize: 14 }}>/mo</div>
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "32px 0 36px", fontSize: 14 }}>
                    {(p.bullets || []).map((f) => (
                      <li key={f} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12, lineHeight: 1.55, color: ink }}>
                        <Check size={16} style={{ marginTop: 2, color: accent, flexShrink: 0 }} strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate("/book")}
                    data-testid={`plan-cta-${p.key}`}
                    style={{
                      background: isBest ? accent : "transparent",
                      color: isBest ? T.cream : T.ink,
                      border: isBest ? "none" : `1px solid ${T.ink}`,
                      padding: "14px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 8, width: "100%",
                      justifyContent: "center", transition: "background 160ms, color 160ms",
                    }}
                  >
                    Start with {p.name} <ArrowUpRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF QUOTE */}
      <section style={{ padding: "112px 0" }}>
        <div style={container()}>
          <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 24 }}>
              {[0,1,2,3,4].map((i) => <Star key={i} size={14} fill={T.coral} color={T.coral} strokeWidth={0} />)}
            </div>
            <blockquote style={{
              fontFamily: FONT_DISPLAY, fontSize: 44, lineHeight: 1.18,
              letterSpacing: "-0.02em", fontWeight: 400, margin: 0, color: T.ink,
            }}>
              <span style={{ color: T.coral, fontStyle: "italic" }}>&ldquo;</span>
              We booked 43 extra jobs the first month.
              The AI answers calls I would&apos;ve missed on the truck.
              <span style={{ color: T.coral, fontStyle: "italic" }}>&rdquo;</span>
            </blockquote>
            <div style={{ marginTop: 32, fontSize: 14, color: T.inkMuted, fontFamily: FONT_MONO, letterSpacing: "0.06em" }}>
              MIKE S. · SULLIVAN &amp; SONS PLUMBING · AUSTIN, TX
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqAccordion />

      {/* TESTIMONIAL MARQUEE — social proof just before the claim strip */}
      <TestimonialMarquee />

      {/* DATABASE REACTIVATION — turn dormant customers into found revenue */}
      <DBReactivationSection />

      {/* INLINE FREE-SITE CLAIM STRIP — warm-cream card just above contact/footer */}
      <FreeSiteClaimStrip />

      {/* CONTACT */}
      <section id="contact" style={{ padding: "56px 0 120px" }}>
        <div style={container()}>
          <div style={{ background: T.ink, color: T.cream, padding: "64px 56px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, background: `radial-gradient(circle, ${T.coral} 0%, transparent 65%)`, opacity: 0.55, borderRadius: "50%" }} />
            <div style={{ position: "absolute", bottom: -100, right: 200, width: 240, height: 240, background: `radial-gradient(circle, ${T.brass} 0%, transparent 65%)`, opacity: 0.25, borderRadius: "50%" }} />

            <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "start" }}>
              <div>
                <div style={{ ...eyebrow(), color: T.brass }}>Book a demo</div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 56, fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 0.98, margin: "16px 0 0", color: T.cream }}>
                  Stop losing the<br />
                  <em style={italicAccent(T.coral)}>phone lottery.</em>
                </h3>
                <p style={{ fontSize: 17, marginTop: 22, maxWidth: 480, lineHeight: 1.6, color: T.inkMuted }}>
                  Book a 15-minute demo. We&apos;ll build your free preview website live, on the call &mdash;
                  and you decide whether we take it from there.
                </p>
                <div style={{ marginTop: 32, fontSize: 13, color: T.inkMuted, fontFamily: FONT_MONO, letterSpacing: "0.06em", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div><PhoneCall size={13} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} color={T.brass} /> No commitment · No card required</div>
                  <div><Check size={13} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} color={T.sage} /> Preview website in 24 hours</div>
                  <div><Check size={13} style={{ display: "inline", marginRight: 8, verticalAlign: "-2px" }} color={T.sage} /> Or chat with our AI right now (bottom-right)</div>
                </div>
              </div>

              {sent ? (
                <div style={{ background: T.canvas, border: `1px solid ${T.line}`, padding: "40px 32px", textAlign: "center" }} data-testid="contact-sent">
                  <Check size={40} color={T.sage} style={{ margin: "0 auto 16px" }} strokeWidth={2} />
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: 28, fontWeight: 400, fontStyle: "italic", color: T.cream }}>You&apos;re on the list.</div>
                  <p style={{ fontSize: 15, color: T.inkMuted, marginTop: 12, lineHeight: 1.6 }}>
                    We&apos;ll be in touch within one business day &mdash; often within an hour if it&apos;s a weekday.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} style={{ display: "grid", gap: 12 }} data-testid="contact-form">
                  <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={input()} data-testid="contact-name" />
                  <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required style={input()} data-testid="contact-email" />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={input()} data-testid="contact-phone" />
                    <input placeholder="Business name" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} style={input()} data-testid="contact-business" />
                  </div>
                  <input placeholder="Industry (e.g. plumber, dentist)" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} style={input()} data-testid="contact-industry" />
                  <textarea placeholder="Anything else? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} style={{ ...input(), resize: "vertical" }} data-testid="contact-message" />
                  <button type="submit" disabled={submitting} style={{
                    background: T.coral, color: T.cream, border: "none",
                    padding: "16px 24px", fontSize: 15, fontWeight: 700, cursor: submitting ? "wait" : "pointer",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    opacity: submitting ? 0.7 : 1,
                  }} data-testid="contact-submit">
                    {submitting ? "Sending…" : "Book my demo"} <ArrowUpRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop: `1px solid ${T.lineSoft}`, padding: "56px 0", fontSize: 13, color: T.inkMuted, background: T.panel }}>
        <div style={container()}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
            <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img
                src="/logo.png"
                alt="Clients OnRamp"
                style={{ height: 42, width: "auto", display: "block", objectFit: "contain" }}
              />
            </Link>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <Link to="/preview/hybrid" target="_blank" rel="noopener" style={footerLink()}>See a demo site</Link>
              <Link to="/industries" style={footerLink()}>Industries</Link>
              <Link to="/pricing-public" style={footerLink()}>Pricing</Link>
              <Link to="/packages" style={footerLink()}>Packages</Link>
              <Link to="/benchmarks" style={footerLink()}>Industry benchmarks</Link>
              <Link to="/#database-reactivation" style={footerLink()}>Database Reactivation</Link>
              <Link to="/get-started" style={footerLink()}>Get started</Link>
              <Link to="/onboarding-public" style={footerLink()}>Onboarding</Link>
              <Link to="/privacy" style={footerLink()}>Privacy</Link>
              <Link to="/terms" style={footerLink()}>Terms</Link>
            </div>
            <div style={{ fontSize: 12, color: T.inkFaint, fontFamily: FONT_MONO }}>
              © 2026 Clients OnRamp
            </div>
          </div>
        </div>
      </footer>

      {/* Live AI concierge widget (unchanged behavior) */}
      <PublicChatWidget agent={agent} />

      {/* Sticky "Claim my free site" pill — always reachable */}
      <StickyClaimPill />

      {/* Exit-intent rescue popup — fires once when cursor leaves through top edge */}
      <ExitIntentPopup />
    </div>
  );
}

/** One-time exit-intent modal: fires when the mouse leaves the viewport
 *  through the TOP edge (address bar / close-tab region). Session-storage
 *  guarded so it only shows once per visit. Not shown on mobile (no cursor). */
function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return; // touch → skip
    try { if (sessionStorage.getItem("co_exit_intent_shown") === "1") return; } catch { /* noop */ }
    let armed = false;
    const armTimer = setTimeout(() => { armed = true; }, 8000); // don't fire in first 8s
    const onLeave = (e) => {
      if (!armed) return;
      if (e.clientY > 0) return; // only real top-edge exits
      if (e.relatedTarget || e.toElement) return;
      try { sessionStorage.setItem("co_exit_intent_shown", "1"); } catch { /* noop */ }
      setOpen(true);
      document.removeEventListener("mouseout", onLeave);
    };
    document.addEventListener("mouseout", onLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onLeave);
    };
  }, []);

  // ESC closes the modal (accessibility), but ONLY the ✕ button or ESC — never backdrop clicks
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business.trim() || !form.email.trim()) return toast.error("Business name and email are required.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Enter a valid email.");
    setBusy(true);
    try {
      await api.post("/public/contact", {
        name:     "Exit-intent visitor",
        email:    form.email.trim(),
        phone:    "",
        business: form.business.trim(),
        industry: "",
        message:  "Exit-intent popup — free-site claim (rescued at browser close)",
      });
      setDone(true);
      toast.success("Nice catch — we'll email you shortly.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div
      data-testid="exit-intent-popup"
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "rgba(10,14,26,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "co-fade-in 220ms ease-out both",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#F7F4EE", color: "#0A192F",
          maxWidth: 520, width: "100%",
          padding: "40px 36px", position: "relative",
          border: `1px solid rgba(10,25,48,0.1)`,
          boxShadow: "0 40px 100px -20px rgba(0,0,0,0.6)",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="exit-intent-close"
          style={{ position: "absolute", top: 12, right: 12, background: "rgba(10,25,48,0.05)", border: "1px solid rgba(10,25,48,0.15)", borderRadius: 20, cursor: "pointer", padding: "4px 12px", color: "#5A6478", fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}
        >
          No thanks
        </button>

        {done ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Check size={40} color="#047857" style={{ margin: "0 auto 14px" }} strokeWidth={2} />
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: 26, fontWeight: 500, fontStyle: "italic", color: "#0A192F" }}>Nice catch.</div>
            <p style={{ fontSize: 14, color: "#3D4756", marginTop: 10, lineHeight: 1.55 }}>
              We&apos;ll email your preview site link within one business day.
            </p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.14em", textTransform: "uppercase", color: T.coralDark, fontWeight: 700 }}>
              Wait — before you go
            </div>
            <h3 style={{ fontFamily: FONT_DISPLAY, fontSize: 32, lineHeight: 1.1, letterSpacing: "-0.02em", fontWeight: 400, margin: "12px 0 12px", color: "#0A192F" }}>
              Grab your <em style={{ fontStyle: "italic", color: T.coral }}>free website</em> first.
            </h3>
            <p style={{ fontSize: 14, color: "#3D4756", lineHeight: 1.55, margin: "0 0 22px" }}>
              We&apos;ll build a preview site for your business — no card, no obligation. Yours to keep even if you never subscribe.
            </p>
            <form onSubmit={submit} style={{ display: "grid", gap: 10 }} data-testid="exit-intent-form">
              <input
                required
                value={form.business}
                onChange={(e) => setForm({ ...form, business: e.target.value })}
                placeholder="Business name"
                data-testid="exit-intent-business"
                style={exitInput()}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@yourbusiness.com"
                data-testid="exit-intent-email"
                style={exitInput()}
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="exit-intent-submit"
                style={{
                  marginTop: 6,
                  background: T.coral, color: "#F7F4EE",
                  border: "none", padding: "14px 22px",
                  fontSize: 14, fontWeight: 700,
                  fontFamily: FONT_MONO, letterSpacing: "0.08em", textTransform: "uppercase",
                  cursor: busy ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "Sending…" : "Send me my free site"} <ArrowUpRight size={15} />
              </button>
            </form>
            <p style={{ fontSize: 11, color: "#5A6478", marginTop: 14, textAlign: "center" }}>
              We&apos;ll reach out within one business day. No spam, ever.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
function exitInput() {
  return {
    background: "#FFFFFF", color: "#0A192F",
    border: "1px solid rgba(10,25,48,0.14)",
    padding: "12px 14px", fontSize: 14, fontFamily: "inherit", outline: "none", width: "100%",
  };
}

/** Bottom-right sticky pill: scroll to the claim strip from any position. */
function StickyClaimPill() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = () => {
    const strip = document.querySelector('[data-testid="free-site-claim-strip"]');
    if (strip) {
      strip.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        const first = document.querySelector('[data-testid="claim-business"]');
        if (first) first.focus();
      }, 600);
    }
  };
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={go}
      data-testid="sticky-claim-pill"
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 22,
        zIndex: 40,
        background: "#0A192F",
        color: "#F7F4EE",
        border: `1px solid ${T.coral}`,
        padding: "12px 20px",
        borderRadius: 999,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 700,
        fontFamily: FONT_MONO,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        boxShadow: "0 14px 34px -10px rgba(255,107,71,0.55), 0 4px 12px rgba(0,0,0,0.3)",
        animation: "co-fade-in 220ms ease-out both",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 999, background: T.sage, boxShadow: `0 0 0 3px rgba(74,222,128,0.18)` }} />
      Claim my free site <ArrowUpRight size={14} />
    </button>
  );
}

/* ---------- helpers ---------- */
function normalizePlans(rawPlans) {
  return rawPlans.map((p) => ({
    key:     p.key,
    name:    p.name,
    price:   p.price,
    tag:     p.tag,
    bullets: p.bullets || [],
    best:    !!p.best,
  }));
}

function container() { return { maxWidth: 1200, margin: "0 auto", padding: "0 28px" }; }
function eyebrow()   { return { fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.coral, fontWeight: 600 }; }
function h2()        { return { fontFamily: FONT_DISPLAY, fontSize: 64, letterSpacing: "-0.03em", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 0", color: T.ink }; }
function italicAccent(color) { return { fontStyle: "italic", color, fontWeight: 400 }; }
function navLink()   { return { color: T.inkMuted, background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 14, fontFamily: "inherit" }; }
function btnCoral()  { return { background: T.coral, color: T.cream, border: "none", padding: "11px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }; }
function btnInk()    { return { background: T.ink, color: T.cream, border: "none", padding: "11px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }; }
function btnGhost()  { return { background: "transparent", color: T.ink, border: `1px solid ${T.line}`, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }; }
function input()     { return { background: T.canvas, color: T.cream, border: `1px solid ${T.line}`, padding: "12px 14px", fontSize: 14, fontFamily: "inherit", outline: "none", width: "100%" }; }
function trustChip() {
  return {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: "7px 16px", border: `1px solid ${T.line}`, background: T.panel, borderRadius: 999,
    fontSize: 12, fontFamily: FONT_MONO, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkMuted,
  };
}
function footerLink() { return { color: T.inkMuted, textDecoration: "none" }; }

// -----------------------------------------------------------------------------
// Live "what ChatGPT is telling your customers" widget
// -----------------------------------------------------------------------------
function CompetitorDemoWidget() {
  const [industry, setIndustry] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [revealed, setRevealed] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!industry.trim() || !city.trim()) return;
    setBusy(true);
    setResult(null);
    setRevealed(0);
    try {
      const { data } = await api.post(
        `/public/competitor-demo`,
        { industry: industry.trim(), city: city.trim() },
      );
      setResult(data);
      // Staggered reveal of each message
      let step = 0;
      const total = 1 + (data.recommendations?.length || 0) + 1;
      const interval = setInterval(() => {
        step += 1;
        setRevealed(step);
        if (step >= total) clearInterval(interval);
      }, 550);
    } catch (err) {
      // Intentional silent fail — the form stays clean and user sees no scary error.
      console.debug("PublicLanding form submit swallowed:", err?.message || err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: "#0B1220", border: `1px solid ${T.line}`, padding: 20 }} data-testid="competitor-demo-widget">
      <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 16 }}>
        <input
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="Your industry (e.g. plumber)"
          data-testid="cd-industry"
          style={{ background: "#141B2D", border: `1px solid ${T.line}`, color: T.ink, padding: "10px 12px", fontSize: 14, outline: "none" }}
        />
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City (e.g. Los Angeles, CA)"
          data-testid="cd-city"
          style={{ background: "#141B2D", border: `1px solid ${T.line}`, color: T.ink, padding: "10px 12px", fontSize: 14, outline: "none" }}
        />
        <button type="submit" disabled={busy || !industry || !city} data-testid="cd-submit"
                style={{ background: T.coral, color: "#FFF", border: "none", padding: "10px 18px", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", opacity: (busy || !industry || !city) ? 0.6 : 1 }}>
          {busy ? <><Loader2 size={14} className="animate-spin" /> Asking AI…</> : <><Send size={14} /> Ask AI</>}
        </button>
      </form>

      {result && (
        <div style={{ background: "#0F1729", border: `1px solid ${T.line}`, padding: 20, fontSize: 14, lineHeight: 1.6 }}>
          {/* User bubble */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: T.brass, color: "#0A0E1A", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0, fontSize: 12 }}>U</div>
            <div style={{ background: "#1E293B", color: T.ink, padding: "10px 14px", borderRadius: 12, maxWidth: "80%" }}>{result.user_question}</div>
          </div>
          {/* Assistant intro */}
          {revealed >= 1 && (
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#10A37F,#0D8A6C)", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0, fontSize: 12 }}>AI</div>
              <div style={{ color: T.inkSoft, padding: "6px 0", maxWidth: "85%" }}>{result.assistant_intro}</div>
            </div>
          )}
          {/* Recommendations, staggered */}
          {result.recommendations.slice(0, revealed - 1).map((rec, i) => (
            <div key={i} style={{ marginLeft: 42, marginBottom: 10, padding: "10px 14px", background: "#141B2D", border: `1px solid ${T.line}`, borderLeft: `3px solid ${T.coral}` }}>
              <div style={{ color: T.ink, fontWeight: 600, marginBottom: 3 }}>{i + 1}. {rec.name}</div>
              <div style={{ color: T.inkMuted, fontSize: 13 }}>{rec.why}</div>
            </div>
          ))}
          {/* Outro */}
          {revealed >= 1 + result.recommendations.length + 1 && (
            <div style={{ marginLeft: 42, marginTop: 10, color: T.inkSoft, fontSize: 13, fontStyle: "italic" }}>{result.assistant_outro}</div>
          )}
          {/* Punchline */}
          {revealed >= 1 + result.recommendations.length + 1 && (
            <div style={{ marginTop: 24, padding: 18, background: "#1A0F0C", border: `1px solid ${T.coral}66`, borderLeft: `4px solid ${T.coral}` }} data-testid="cd-punchline">
              <div style={{ color: T.coral, fontSize: 11, fontFamily: FONT_MONO, letterSpacing: 2, fontWeight: 700, marginBottom: 6 }}>NOTICE ANYTHING?</div>
              <div style={{ color: T.ink, fontSize: 15, lineHeight: 1.55 }}>
                <strong>Your business isn't in that list.</strong> Every day, AI assistants send customers to your competitors instead of you. The good news: it's fixable — usually in under 30 days.
              </div>
              <Link to="/audit" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, background: T.coral, color: "#FFF", padding: "10px 18px", fontWeight: 700, fontSize: 14, textDecoration: "none" }} data-testid="cd-audit-cta">
                Get my free audit teaser <ArrowUpRight size={14} />
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
